import React, { useState, useEffect, useCallback } from "react";
import { useForm, useFieldArray, FormProvider, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildingAddressService } from "../../../services/buildingAddressService";
import { Button } from "../../../shadcn/ui/button";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { getCurrencyForCountry } from "../../../utils/countryCurrencyMap";
import { getUser } from "../../../services/localService";
import { useHistory, useLocation, useParams } from "react-router-dom";
import toastUtils from "../../../utils/toast";
import { showApiMessage } from "../../../utils/messageHandler";
import { Edit3, Eye, Trash2, ArrowLeft, Upload, Image } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shadcn/ui/card";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import logger from "../../../utils/logger";
import { useServiceCategories } from "../../../hooks/useServiceCategories";
import { useUserCurrency } from "../../../hooks/useUserCurrency";

import { addSpaceSchema, defaultFormValues } from "./schema";
import DeleteSpaceModal from "./components/DeleteSpaceModal";

import {
  SpaceTypeAttributesCard,
  PricingBookingRulesCard,
  SpaceInstancesCard,
  SpaceAddonsCard,
  GlobalSettingsCard,
} from "./components";

import AvailabilityUtils from "../../../utils/availability/AvailabilityUtils";

const HostAddSpace = () => {
  // PERF: Start timing the component render
  const renderStart = React.useRef(performance.now());

  React.useEffect(() => {
    const duration = performance.now() - renderStart.current;
    logger.log(`⏱️ [PERF] HostAddSpace: First render took ${duration.toFixed(2)}ms`);
    console.time("⏱️ [PERF] HostAddSpace: Full Interactive Load");

    return () => {
      console.timeEnd("⏱️ [PERF] HostAddSpace: Full Interactive Load");
    };
  }, []);

  const history = useHistory();
  const location = useLocation();
  const { spaceId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingSpace, setFetchingSpace] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [spaceData, setSpaceData] = useState(null);
  const [forceSubcategoryLoad, setForceSubcategoryLoad] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletedInstanceIds, setDeletedInstanceIds] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [originalAvailability, setOriginalAvailability] = useState(() =>
    JSON.parse(JSON.stringify(defaultFormValues.availability))
  );

  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);
  const isBatchUpdating = React.useRef(false);
  const availabilityDefaultsApplied = React.useRef(false);
  const fetchFloorsPromises = React.useRef({});
  const [uploadingDisplayImage, setUploadingDisplayImage] = useState(false);
  const displayImageInputRef = React.useRef(null);

  // Load service categories from Redux (cached) - Don't block render
  const { serviceCategories, serviceCategoriesLoading } = useServiceCategories();

  // Get user's currency based on their country
  const userCurrency = useUserCurrency();
  logger.log("gjfkkrjejrjr [host-add-space] 💰 useUserCurrency returned:", userCurrency);

  // Get type from URL query param (work, stay, or play)
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get("type");
  const lockedCategory = typeParam ? typeParam.toUpperCase() : null;

  // Redirect to Play creation flow if type is play
  useEffect(() => {
    if (typeParam && typeParam.toLowerCase() === "play" && !spaceId) {
      history.replace("/host/play-services/create");
    }
  }, [typeParam, spaceId, history]);

  // Modify default values for HOST_PROPERTY_DISTRIBUTION users
  const currentUser = getUser();
  const isPropertyDistribution = currentUser?.organization?.type?.code === "HOST_PROPERTY_DISTRIBUTION";

  // Find categoryId from locked category
  const getCategoryIdFromCode = categoryCode => {
    if (!categoryCode || !serviceCategories || serviceCategories.length === 0) return undefined;
    const category = serviceCategories.find(cat => cat.code === categoryCode || cat.name === categoryCode);
    return category?.id;
  };

  const lockedCategoryId = lockedCategory ? getCategoryIdFromCode(lockedCategory) : undefined;

  const modifiedDefaultValues = {
    ...defaultFormValues,
    isPublic: isPropertyDistribution ? true : defaultFormValues.isPublic,
    // Pre-fill category if locked
    ...(lockedCategory && {
      category: lockedCategory,
      categoryId: lockedCategoryId,
      // For STAY spaces, enable weekends by default and set check-in/check-out times
      ...(lockedCategory === "STAY" && {
        availability: {
          monday: { enabled: true, startTime: "00:00", endTime: "23:59" },
          tuesday: { enabled: true, startTime: "00:00", endTime: "23:59" },
          wednesday: { enabled: true, startTime: "00:00", endTime: "23:59" },
          thursday: { enabled: true, startTime: "00:00", endTime: "23:59" },
          friday: { enabled: true, startTime: "00:00", endTime: "23:59" },
          saturday: { enabled: true, startTime: "00:00", endTime: "23:59" },
          sunday: { enabled: true, startTime: "00:00", endTime: "23:59" },
        },
      }),
    }),
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    trigger,
    reset,
  } = useForm({
    resolver: zodResolver(addSpaceSchema),
    defaultValues: modifiedDefaultValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [buildingFloors, setBuildingFloors] = useState({}); // Cache floors by building ID
  const [loadingFloors, setLoadingFloors] = useState({});
  const watchedInstances = useWatch({ control, name: "instances" }) || [];

  // Fetch floors for a specific building
  const fetchFloorsForBuilding = useCallback(
    async buildingId => {
      if (!buildingId || buildingFloors[buildingId]) return; // Already cached

      // Deduplicate concurrent in-flight requests for the same building
      if (fetchFloorsPromises.current[buildingId]) {
        return fetchFloorsPromises.current[buildingId];
      }

      const promise = (async () => {
        try {
          setLoadingFloors(prev => ({ ...prev, [buildingId]: true }));
          const response = await buildingAddressService.getBuildingAddressById(buildingId);

          if (response.status === 200 && response.data) {
            const floors = Array.isArray(response.data.floors) ? response.data.floors : [];
            setBuildingFloors(prev => ({
              ...prev,
              [buildingId]: floors,
            }));
          }
        } catch (error) {
          logger.error("Error fetching floors:", error);
          setBuildingFloors(prev => ({
            ...prev,
            [buildingId]: [],
          }));
        } finally {
          setLoadingFloors(prev => ({ ...prev, [buildingId]: false }));
          delete fetchFloorsPromises.current[buildingId];
        }
      })();

      fetchFloorsPromises.current[buildingId] = promise;
      return promise;
    },
    [buildingFloors]
  );

  // Prefetch floors for any preselected building addresses
  useEffect(() => {
    if (!Array.isArray(watchedInstances)) return;
    const uniqueBuildingIds = Array.from(
      new Set(
        watchedInstances
          .map(inst => (inst && inst.buildingAddressId ? Number(inst.buildingAddressId) : null))
          .filter(id => !!id)
      )
    );
    uniqueBuildingIds.forEach(id => fetchFloorsForBuilding(id));
  }, [watchedInstances, fetchFloorsForBuilding]);

  // AUTOMATIC FLOOR ID SYNC
  useEffect(() => {
    if (!Array.isArray(watchedInstances) || Object.keys(buildingFloors).length === 0) return;

    watchedInstances.forEach((instance, index) => {
      if (!instance || !instance.buildingAddressId || !instance.floorLevel) return;

      const floors = buildingFloors[instance.buildingAddressId] || [];
      if (floors.length === 0) return;

      const matchedFloor = floors.find(f => f.floorNumber?.toString() === instance.floorLevel);

      // If we found a floor and the current floorId is different or missing, sync it
      if (matchedFloor && matchedFloor.id !== instance.floorId) {
        setValue(`instances.${index}.floorId`, matchedFloor.id, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    });
  }, [buildingFloors, watchedInstances, setValue]);

  const {
    fields: instanceFields,
    append: appendInstance,
    remove: removeInstance,
    replace,
  } = useFieldArray({
    control,
    name: "instances",
  });

  const watchedGenerationMode = useWatch({ control, name: "instanceGenerationMode" });
  const watchedQuantity = useWatch({ control, name: "instanceQuantity" });

  const watchedImages = useWatch({ control, name: "images", defaultValue: [] });
  const watchedBookingDuration = useWatch({ control, name: "bookingDuration", defaultValue: "hourly" });
  const watchedCategory = useWatch({ control, name: "category" });

  // Consolidate time update logic for Category and Duration changes
  useEffect(() => {
    if (!watchedCategory && !watchedBookingDuration) return;

    const categoryName = AvailabilityUtils.getServiceCategory(watchedCategory);
    if (!categoryName) return;

    const duration = watchedBookingDuration || "hourly";
    const constraints = AvailabilityUtils.getTimeConstraints(categoryName, duration.toUpperCase());
    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

    // Set flag to suspend watch during batch update
    isBatchUpdating.current = true;

    days.forEach(day => {
      const currentStart = getValues(`availability.${day}.startTime`);
      const currentEnd = getValues(`availability.${day}.endTime`);

      // 1. Set default times if current values are empty (initial setup)
      // This makes it a default but allows the user to change it to what they want
      if (!currentStart) {
        setValue(`availability.${day}.startTime`, constraints.startTime, { shouldDirty: false });
      }
      if (!currentEnd) {
        setValue(`availability.${day}.endTime`, constraints.endTime, { shouldDirty: false });
      }
    });

    // 2. Auto-enable all days for standard categories, but ONLY during initial setup
    // (create flow, before the user has interacted). Never re-enable days the user
    // toggled off, and never override availability loaded from an existing space.
    const isEditMode = !!spaceId;
    if (!isEditMode && !availabilityDefaultsApplied.current) {
      days.forEach(day => {
        const currentEnabled = getValues(`availability.${day}.enabled`);
        if (categoryName === "WORK" || categoryName === "STAY") {
          if (!currentEnabled) {
            setValue(`availability.${day}.enabled`, true, { shouldDirty: true, shouldValidate: true });
          }
        }
      });
      availabilityDefaultsApplied.current = true;
    }

    // Re-enable watch after batch complete
    setTimeout(() => {
      isBatchUpdating.current = false;
    }, 0);
  }, [watchedCategory, watchedBookingDuration, setValue, getValues, spaceId]);

  // Bulk Instance Generation logic
  useEffect(() => {
    if (watchedGenerationMode !== "BULK" || isBatchUpdating.current || watchedQuantity === "") return;

    const quantity = parseInt(watchedQuantity) || 1;
    const currentInstances = getValues("instances") || [];
    const template = currentInstances[0];

    if (!template) return;

    // 1. Handle Quantity Change (requires replace to resize array)
    // Only proceed if count actually changed.
    if (currentInstances.length !== quantity) {
      // Shrinking the count drops instances off the end — mark any already
      // persisted ones for deletion so the backend removes them on save.
      if (quantity < currentInstances.length) {
        const droppedApiIds = currentInstances
          .slice(quantity)
          .map(inst => inst?.apiId)
          .filter(Boolean);
        if (droppedApiIds.length > 0) {
          setDeletedInstanceIds(prev => Array.from(new Set([...prev, ...droppedApiIds])));
        }
      }

      const newInstances = [];
      for (let i = 0; i < quantity; i++) {
        if (i === 0) {
          newInstances.push({ ...template, id: template.id || `bulk-init-${Date.now()}` });
        } else {
          const existing = currentInstances[i];
          if (existing?.apiId) {
            // Already-persisted instance — keep its own data, don't clone the template onto it
            newInstances.push(existing);
          } else {
            newInstances.push({
              id: existing?.id?.toString().startsWith("existing-")
                ? existing.id
                : existing?.id || `bulk-${Date.now()}-${i}`,
              apiId: existing?.apiId,
              name: generateInstanceName(i, template.name),
              buildingAddressId: template.buildingAddressId,
              floorLevel: template.floorLevel,
              floorId: template.floorId,
              coordinates: template.coordinates || { latitude: 0, longitude: 0 },
            });
          }
        }
      }
      isBatchUpdating.current = true;
      replace(newInstances);
      setTimeout(() => {
        isBatchUpdating.current = false;
      }, 0);
      return;
    }

    // 2. Handle Template Field Sync (use setValue for indices 1..N to preserve focus on index 0)
    const fieldsToSync = ["buildingAddressId", "floorLevel", "floorId", "coordinates"];

    // Check if any instance needs syncing.
    // Skip already-persisted instances (those with an apiId) — only freshly
    // generated instances should mirror the template.
    const needsSync = currentInstances.some((inst, i) => {
      if (i === 0 || inst.apiId) return false;
      const expectedName = generateInstanceName(i, template.name);
      return inst.name !== expectedName || fieldsToSync.some(f => inst[f] !== template[f]);
    });

    if (needsSync) {
      isBatchUpdating.current = true;
      for (let i = 1; i < quantity; i++) {
        if (currentInstances[i].apiId) continue;

        const expectedName = generateInstanceName(i, template.name);

        if (currentInstances[i].name !== expectedName) {
          setValue(`instances.${i}.name`, expectedName, { shouldDirty: true });
        }

        fieldsToSync.forEach(field => {
          if (currentInstances[i][field] !== template[field]) {
            setValue(`instances.${i}.${field}`, template[field], { shouldDirty: true });
          }
        });
      }
      setTimeout(() => {
        isBatchUpdating.current = false;
      }, 0);
    }
  }, [watchedGenerationMode, watchedQuantity, watchedInstances[0], replace, setValue, getValues]);

  // Sync instanceQuantity when in INDIVIDUAL mode
  useEffect(() => {
    if (watchedGenerationMode === "INDIVIDUAL") {
      const currentCount = watchedInstances.length;
      if (watchedQuantity !== currentCount) {
        setValue("instanceQuantity", currentCount);
      }
    }
  }, [watchedGenerationMode, watchedInstances.length, watchedQuantity, setValue]);

  // Set categoryId when service categories are loaded and we have a locked category
  // Simplified: Set locked category immediately when available
  useEffect(() => {
    if (lockedCategory && serviceCategories?.length > 0 && !spaceId) {
      const categoryId = getCategoryIdFromCode(lockedCategory);
      if (categoryId) {
        setValue("category", lockedCategory, { shouldValidate: false });
        setValue("categoryId", categoryId, { shouldValidate: false });
      }
    }
  }, [lockedCategory, serviceCategories, spaceId]);

  // Check if we're in edit mode and fetch space data
  useEffect(() => {
    if (spaceId) {
      setIsEditMode(true);
      setIsReadOnly(true);
      fetchSpaceData();
    } else {
      setIsEditMode(false);
      setIsReadOnly(false);
    }
  }, [spaceId]);

  // Simplified: Only validate when user switches to edit mode
  useEffect(() => {
    if (!isReadOnly && isEditMode && spaceData) {
      // Defer validation to avoid blocking render
      requestAnimationFrame(() => trigger());
    }
  }, [isReadOnly, isEditMode]);

  const fetchSpaceData = async () => {
    try {
      console.time("⏱️ [PERF] fetchSpaceData");
      setFetchingSpace(true);
      const currentUser = getUser();
      if (!currentUser) {
        toastUtils.error("Authentication required", {
          description: "Please log in to continue.",
        });
        history.push("/signin");
        return;
      }

      const response = await spaceManagementService.getSpaceById(parseInt(spaceId), currentUser.id);

      if (response.status === 200 && response.data) {
        const spaceData = response.data;
        setSpaceData(spaceData);
        setDeletedInstanceIds([]);

        // Pre-populate buildingFloors from API response to avoid loading state
        if (spaceData.instances && spaceData.instances.length > 0) {
          const floorsFromApi = {};
          spaceData.instances.forEach(instance => {
            const buildingAddress = instance.location?.buildingAddress;
            if (buildingAddress?.id && buildingAddress.floors) {
              floorsFromApi[buildingAddress.id] = buildingAddress.floors;
            }
          });
          if (Object.keys(floorsFromApi).length > 0) {
            setBuildingFloors(prev => ({ ...prev, ...floorsFromApi }));
          }
        }

        const transformedData = transformApiResponseToFormData(spaceData);
        reset(transformedData);
        setOriginalAvailability(JSON.parse(JSON.stringify(transformedData.availability)));

        // Force subcategory loading after form reset
        if (transformedData.category && transformedData.categoryId) {
          setTimeout(() => {
            setValue("category", transformedData.category);
            setValue("categoryId", transformedData.categoryId);
            setValue("subCategory", transformedData.subCategory);
            setValue("subCategoryId", transformedData.subCategoryId);
            trigger(["category", "categoryId", "subCategory", "subCategoryId"]);
            setForceSubcategoryLoad(transformedData.category);
          }, 200);
        }

        showApiMessage(response, "load space");
      } else {
        throw new Error("Failed to fetch space data");
      }
    } catch (error) {
      logger.error("Error fetching space:", error);
      showApiMessage(error, "load space");
      history.push("/host/spaces");
    } finally {
      console.timeEnd("⏱️ [PERF] fetchSpaceData");
      setFetchingSpace(false);
    }
  };

  const transformApiResponseToFormData = apiData => {
    const tStart = performance.now();
    logger.log("🔍 [DEBUG] Transforming API response to form data:", apiData);
    logger.log("🔍 [DEBUG] API media object:", apiData.media);
    logger.log("🔍 [DEBUG] API category object:", apiData.category);
    logger.log("🔍 [DEBUG] API subCategory object:", apiData.subCategory);
    logger.log("gjfkkrjejrjr [host-add-space] 💰 API currency:", apiData.currency);
    logger.log("gjfkkrjejrjr [host-add-space] 💰 User currency from hook:", userCurrency);

    // Helper to convert time format from "09:00:00" to "09:00"
    const formatTime = timeStr => {
      if (!timeStr) {
        const categoryName = AvailabilityUtils.getServiceCategory(apiData.spaceType);
        const constraints = AvailabilityUtils.getTimeConstraints(categoryName, apiData.duration?.toUpperCase());
        return constraints.startTime;
      }
      return timeStr.substring(0, 5); // Remove seconds
    };

    // Transform availability slots to form format
    const transformAvailability = () => {
      const duration = apiData.duration ? apiData.duration.toLowerCase() : "hourly";
      const isDailyOrMonthly = duration === "daily" || duration === "monthly";

      // Get dynamic defaults based on category and duration
      const categoryName = AvailabilityUtils.getServiceCategory(apiData.spaceType);
      const constraints = AvailabilityUtils.getTimeConstraints(categoryName, duration.toUpperCase());
      const defaultStartTime = constraints.startTime;
      const defaultEndTime = constraints.endTime;

      const defaultDay = {
        enabled: false,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      };

      const availability = {
        monday: { ...defaultDay },
        tuesday: { ...defaultDay },
        wednesday: { ...defaultDay },
        thursday: { ...defaultDay },
        friday: { ...defaultDay },
        saturday: { ...defaultDay },
        sunday: { ...defaultDay },
      };

      // Map API availableSlots to form structure
      if (apiData.availableSlots) {
        apiData.availableSlots.forEach(slot => {
          const dayKey = slot.day.toLowerCase();
          if (availability[dayKey]) {
            availability[dayKey] = {
              enabled: true,
              startTime: formatTime(slot.startTime),
              endTime: formatTime(slot.endTime),
            };
          }
        });
      }

      return availability;
    };

    // Transform instances to form format
    const transformInstances = () => {
      if (!apiData.instances || apiData.instances.length === 0) {
        return defaultFormValues.instances;
      }

      return apiData.instances.map((instance, index) => {
        // Try to get floor number from multiple possible sources
        const floorNumber =
          instance.floor?.floorNumber ?? // From floor object
          instance.location?.floorLevel ?? // From location
          null;

        return {
          id: `existing-${instance.id}`,
          apiId: instance.id,
          name: instance.title || instance.location?.name || `Unit ${index + 1}`,
          buildingAddressId: instance.location?.buildingAddress?.id || null,
          floorLevel: floorNumber !== null && floorNumber !== undefined ? floorNumber.toString() : "",
          floorId: instance.floor?.id || instance.floorId || null,
          coordinates: {
            latitude: instance.location?.buildingAddress?.latitude || -1.2921,
            longitude: instance.location?.buildingAddress?.longitude || 36.8219,
          },
        };
      });
    };

    // Transform images to form format
    const transformImages = () => {
      const images = apiData.media?.images || apiData.images || [];
      logger.log("🔍 [DEBUG] Transform images - apiData.media:", apiData.media);
      logger.log("🔍 [DEBUG] Transform images - images array:", images);

      if (!images || images.length === 0) {
        logger.log("🔍 [DEBUG] No images found in API response");
        return [];
      }

      const transformedImages = images.map((imageUrl, index) => ({
        id: Date.now() + index,
        file: null, // No file object for existing images
        url: imageUrl,
        name: `image_${index + 1}.jpg`,
        size: 0, // Unknown size for existing images
      }));

      logger.log("🔍 [DEBUG] Transformed images:", transformedImages);
      return transformedImages;
    };

    const tEnd = performance.now();
    logger.log(`⏱️ [PERF] transformApiResponseToFormData took ${(tEnd - tStart).toFixed(2)}ms`);
    return {
      // Basic space information
      // spaceType is not in form but always passed as "LOW" in payload
      category: apiData.category?.name?.toUpperCase() || "WORK",
      categoryId: apiData.category?.id || undefined,
      subCategory: apiData.subCategory?.name || undefined,
      subCategoryId: apiData.subCategory?.id || undefined,
      capacity: (apiData.capacity || 1).toString(),
      size: apiData.size || "40 sqm",
      description: (apiData.description || "").slice(0, 250),

      // Pricing information
      currency: userCurrency.toLowerCase(),
      basePrice: (apiData.ratePrice || 0).toString(),
      bookingDuration: apiData.duration ? apiData.duration.toLowerCase() : "hourly", // Read from API response, default to hourly
      minDuration: (apiData.minimumDuration || 1).toString(),
      maxDuration: (apiData.maximumDuration || 24).toString(),
      bufferTime: (apiData.bufferTimeInMinutes ?? 30).toString(),

      // Availability
      availability: transformAvailability(),

      // Host display settings
      hostDisplayName: apiData.hostDisplayName || "",
      hostDisplayImage: apiData.hostDisplayImage || "",

      // Visibility - check the visibility field from API
      isPublic: apiData.visibility === "PUBLIC",
      visibleToCorporate: apiData.visibleToCorporate ?? false,
      visibleToConsumer: apiData.visibleToConsumer ?? false,

      // Arrays - ensure they are arrays of numbers (IDs only)
      amenities: Array.isArray(apiData.amenities)
        ? apiData.amenities
            .map(item => (typeof item === "object" ? item.id : item))
            .filter(id => typeof id === "number")
        : [],
      addons: Array.isArray(apiData.addons)
        ? apiData.addons.map(item => (typeof item === "object" ? item.id : item)).filter(id => typeof id === "number")
        : [],
      images: transformImages(),

      // Instances
      // Always open existing spaces in INDIVIDUAL mode so every instance is
      // visible and editable. BULK mode only renders the template (index 0),
      // which hid the other instances and let the sync effect overwrite them.
      instanceGenerationMode: "INDIVIDUAL",
      instanceQuantity: apiData.instances?.length || 1,
      instances: transformInstances(),

      // Floor plan (if exists)
      floorPlan: null, // API doesn't provide floor plan data in this structure
    };
  };

  // Cleanup image URLs on unmount
  useEffect(() => {
    return () => {
      const currentImages = getValues("images");
      currentImages.forEach(image => {
        if (image.url && image.url.startsWith("blob:")) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, [getValues]);

  // Aggressive form data restoration on component mount
  useEffect(() => {
    logger.log("🔍 Component mounted, checking for saved form data...");

    const formDataKey = "ADD_SPACE_FORM_DATA";
    let savedData = null;

    try {
      const sessionData = sessionStorage.getItem(formDataKey);
      if (sessionData) {
        savedData = JSON.parse(sessionData);
        logger.log("📦 Found data in sessionStorage:", savedData);
      }
    } catch (e) {
      logger.warn("⚠️ Failed to read from sessionStorage:", e);
    }

    // Restore data if found and not too old (within 2 hours)
    if (savedData && savedData.formData && Date.now() - savedData.timestamp < 7200000) {
      logger.log("🔄 RESTORING FORM DATA:", savedData.formData);

      // Draft availability reflects the user's real choices - don't auto-enable days later
      availabilityDefaultsApplied.current = true;

      setTimeout(() => {
        try {
          // Filter out blob URLs from images
          const restoredData = { ...savedData.formData };
          if (restoredData.images) {
            restoredData.images = restoredData.images.filter(img => img.url && !img.url.startsWith("blob:"));
          }
          reset(restoredData);
          logger.log("✅ Form data restored successfully");

          setTimeout(() => {
            trigger();
          }, 200);
        } catch (e) {
          logger.error("❌ Failed to restore form data:", e);
        }
      }, 100);
    } else if (savedData) {
      logger.log("⏰ Saved data is too old, cleaning up...");
      sessionStorage.removeItem(formDataKey);
    } else {
      logger.log("📭 No saved form data found");
    }
  }, []); // Run only on mount

  useEffect(() => {
    // Handle floor plan updates and full form restoration from navigation
    if (location.state) {
      const { preservedFormData, updatedFloorPlan } = location.state;

      if (preservedFormData) {
        logger.log("🔄 Restoring full form from navigation state");
        try {
          reset(preservedFormData);
          setTimeout(() => {
            trigger();
          }, 150);
        } catch (e) {
          logger.warn("⚠️ Failed to restore full form from navigation state:", e);
        }
      }

      if (updatedFloorPlan) {
        logger.log("🎨 Updating floor plan from navigation state");
        setValue("floorPlan", updatedFloorPlan);
      }

      if (preservedFormData || updatedFloorPlan) {
        // Clean up state
        setTimeout(() => {
          history.replace({
            ...location,
            state: {
              ...location.state,
              preservedFormData: undefined,
              updatedFloorPlan: undefined,
            },
          });
        }, 100);
      }
    }
  }, [location, history, reset, setValue, trigger]);

  // Convert image file to base64 string
  const convertImageToBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Save form data whenever form values change (auto-save)
  useEffect(() => {
    let saveTimeout;
    const subscription = watch(value => {
      // Skip if batch updating
      if (isBatchUpdating.current) return;

      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => {
        const formDataKey = "ADD_SPACE_FORM_DATA";
        const cleanFormData = { ...value };

        if (cleanFormData.images && Array.isArray(cleanFormData.images)) {
          cleanFormData.images = cleanFormData.images.map(image => ({
            id: image.id,
            name: image.name,
            size: image.size,
            url: image.url && !image.url.startsWith("blob:") ? image.url : null,
          }));
        }

        const dataToSave = {
          formData: cleanFormData,
          timestamp: Date.now(),
          fromPath: location.pathname + location.search,
        };

        try {
          sessionStorage.setItem(formDataKey, JSON.stringify(dataToSave));
        } catch (e) {
          logger.warn("⚠️ Failed to auto-save form data:", e);
        }
      }, 500);
    });

    return () => {
      clearTimeout(saveTimeout);
      subscription.unsubscribe();
    };
  }, [watch, location.pathname, location.search]);

  // Cleanup function - clear saved data when component unmounts (unless going to floor plan)
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/floor-plan-editor/")) {
        logger.log("🧹 Cleaning up saved form data on unmount");
        try {
          sessionStorage.removeItem("ADD_SPACE_FORM_DATA");
        } catch (e) {
          logger.warn("⚠️ Failed to cleanup saved data:", e);
        }
      }
    };
  }, []);

  // Floor plan editing now handled in ManageFloorsDialog via floor plan editor

  const toggleEditMode = () => {
    setIsReadOnly(!isReadOnly);

    // Trigger validation when switching to edit mode to ensure form is valid
    if (isReadOnly) {
      // Switching to edit mode, trigger validation
      setTimeout(() => {
        trigger();
      }, 100);
    }
  };

  const handleDeleteSpace = () => {
    if (!spaceId || !spaceData) return;
    setShowDeleteModal(true);
  };

  const confirmDeleteSpace = async () => {
    if (!spaceId || !spaceData) return;

    try {
      setDeleting(true);
      const currentUser = getUser();
      if (!currentUser) {
        throw new Error("User not found");
      }

      const response = await spaceManagementService.deleteSpace(parseInt(spaceId), currentUser.id);

      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "delete space");
        setShowDeleteModal(false);
        history.push("/host/spaces");
      } else {
        throw new Error("Failed to delete space");
      }
    } catch (error) {
      logger.error("Error deleting space:", error);
      showApiMessage(error, "delete space");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteSpace = () => {
    setShowDeleteModal(false);
  };

  const handlePublishToggle = async () => {
    if (!spaceId || !spaceData) return;

    try {
      setPublishing(true);
      const currentUser = getUser();
      if (!currentUser) {
        throw new Error("User not found");
      }

      if (spaceData.status === "PUBLISHED") {
        const resp = await spaceManagementService.unpublishSpace(parseInt(spaceId), currentUser.id);
        setSpaceData(prev => ({ ...prev, status: "UNPUBLISHED" }));
        showApiMessage(resp, "unpublish space");
      } else {
        const resp = await spaceManagementService.publishSpace(parseInt(spaceId), currentUser.id);
        setSpaceData(prev => ({ ...prev, status: "PUBLISHED" }));
        showApiMessage(resp, "publish space");
      }
    } catch (error) {
      logger.error("Error toggling space status:", error);
      showApiMessage(error, "update space status");
    } finally {
      setPublishing(false);
    }
  };

  const getAlphabetLabel = index => {
    let label = "";
    let n = index;
    while (n >= 0) {
      label = String.fromCharCode((n % 26) + 65) + label;
      n = Math.floor(n / 26) - 1;
    }
    return label;
  };

  const generateInstanceName = (index, baseName) => {
    // If baseName ends with a space and letters (e.g. "Space A" or "Space AA"), try to extract the prefix
    let prefix = baseName || "Office Space";
    const suffixMatch = prefix.match(/\s([A-Z]+)$/);

    if (suffixMatch) {
      // Remove the existing suffix so we can add the new one
      prefix = prefix.substring(0, prefix.length - suffixMatch[0].length);
    }

    const roomLetter = getAlphabetLabel(index);
    return `${prefix} ${roomLetter}`;
  };

  const addInstance = () => {
    if (isReadOnly) return;

    const newInstance = {
      id: `new-${Date.now()}`,
      name: "",
      buildingAddressId: null,
      floorLevel: "",
      floorId: null,
      coordinates: { latitude: 0, longitude: 0 },
    };
    appendInstance(newInstance);
  };

  const removeInstanceAtIndex = index => {
    if (isReadOnly || instanceFields.length <= 1) return;

    const instanceToRemove = instanceFields[index];
    if (instanceToRemove && instanceToRemove.apiId) {
      setDeletedInstanceIds(prev => [...prev, instanceToRemove.apiId]);
    }

    removeInstance(index);

    // If in BULK mode, update the quantity to match the new count
    // to prevent the useEffect from re-adding instances
    if (getValues("instanceGenerationMode") === "BULK") {
      setValue("instanceQuantity", instanceFields.length - 1, { shouldDirty: true });
    }
  };

  const handleFiles = files => {
    if (isReadOnly) return;

    logger.log("📸 [DEBUG] handleFiles called with:", files.length, "files");

    const validImageTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const maxFileSize = 5 * 1024 * 1024; // 5MB limit for API upload
    const maxImages = 8;

    const currentImages = getValues("images");
    logger.log("📸 [DEBUG] Current images:", currentImages.length);

    const remainingSlots = maxImages - currentImages.length;
    logger.log("📸 [DEBUG] Remaining slots:", remainingSlots);

    if (remainingSlots <= 0) {
      logger.log("📸 [DEBUG] Maximum images reached");
      toastUtils.warning("Maximum images reached", {
        description: "You can only upload up to 8 images.",
      });
      return;
    }

    let oversizedFiles = [];
    let invalidTypeFiles = [];

    const validFiles = Array.from(files)
      .slice(0, remainingSlots)
      .filter(file => {
        if (!validImageTypes.includes(file.type)) {
          invalidTypeFiles.push(file.name);
          return false;
        }
        if (file.size > maxFileSize) {
          oversizedFiles.push(file.name);
          return false;
        }
        return true;
      });

    if (oversizedFiles.length > 0) {
      toastUtils.error("File size too large", {
        description: `${oversizedFiles.length} file(s) exceed the 5MB limit`,
      });
    }

    if (invalidTypeFiles.length > 0) {
      toastUtils.error("Invalid file type", {
        description: `${invalidTypeFiles.length} file(s) are not supported. Please upload only image files.`,
      });
    }

    if (files.length > remainingSlots) {
      toastUtils.warning("Some files skipped", {
        description: `Only ${remainingSlots} more images can be added. ${files.length - remainingSlots} files were skipped.`,
      });
    }

    if (validFiles.length > 0) {
      logger.log("📸 [DEBUG] Processing valid files:", validFiles.length);

      const newImages = validFiles.map((file, index) => {
        const imageObj = {
          id: Date.now() + Math.random() + index,
          file: file,
          url: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
        };
        logger.log("📸 [DEBUG] Created image object:", { id: imageObj.id, name: imageObj.name, size: imageObj.size });
        return imageObj;
      });

      const updatedImages = [...currentImages, ...newImages];
      logger.log("📸 [DEBUG] Setting images to:", updatedImages.length, "total images");

      setValue("images", updatedImages);
      trigger("images");

      toastUtils.success("Images added", {
        description: `${newImages.length} image(s) ready to upload`,
      });
    } else {
      logger.log("📸 [DEBUG] No valid files to process");
    }
  };

  const removeImage = imageId => {
    if (isReadOnly) return;

    logger.log("📸 [DEBUG] removeImage called for ID:", imageId);

    const currentImages = getValues("images");
    logger.log("📸 [DEBUG] Current images before removal:", currentImages.length);

    const imageToRemove = currentImages.find(img => img.id === imageId);
    logger.log("📸 [DEBUG] Image to remove found:", !!imageToRemove);

    if (imageToRemove && imageToRemove.url && imageToRemove.url.startsWith("blob:")) {
      logger.log("📸 [DEBUG] Revoking blob URL");
      URL.revokeObjectURL(imageToRemove.url);
    }

    const updatedImages = currentImages.filter(img => img.id !== imageId);
    logger.log("📸 [DEBUG] Updated images after removal:", updatedImages.length);

    setValue("images", updatedImages);
    trigger("images");
  };

  const handleDrag = e => {
    if (isReadOnly) return;

    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = e => {
    if (isReadOnly) return;

    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = e => {
    if (isReadOnly) return;

    logger.log("📸 [DEBUG] handleFileSelect called", { files: e.target.files });

    if (e.target.files && e.target.files.length > 0) {
      logger.log(
        "📸 [DEBUG] Processing files:",
        Array.from(e.target.files).map(f => ({ name: f.name, size: f.size, type: f.type }))
      );
      handleFiles(e.target.files);
    } else {
      logger.log("📸 [DEBUG] No files selected");
    }
  };

  const dayNameToApiDay = dayName => {
    const dayMap = {
      monday: "MONDAY",
      tuesday: "TUESDAY",
      wednesday: "WEDNESDAY",
      thursday: "THURSDAY",
      friday: "FRIDAY",
      saturday: "SATURDAY",
      sunday: "SUNDAY",
    };
    return dayMap[dayName] || dayName.toUpperCase();
  };

  const buildApiPayload = async formData => {
    const currentUser = getUser();
    if (!currentUser) {
      throw new Error("User not found");
    }

    const generateTimeString = (timeString, fallback = "00:00:00") => {
      // Accepts '09:00' or '09:00:00', returns '09:00:00'
      if (!timeString) {
        const parts = fallback.split(":");
        if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
        return fallback;
      }
      const parts = timeString.split(":");
      if (parts.length === 2) return `${parts[0]}:${parts[1]}:00`;
      if (parts.length === 3) return `${parts[0]}:${parts[1]}:${parts[2]}`;
      return timeString;
    };

    // Get dynamic defaults for overnight logic
    const categoryName = AvailabilityUtils.getServiceCategory(formData.category);
    const constraints = AvailabilityUtils.getTimeConstraints(categoryName, formData.bookingDuration?.toUpperCase());

    const allAvailabilitySlots = [];
    Object.entries(formData.availability)
      .filter(([_, dayData]) => dayData.enabled)
      .forEach(([dayName, dayData]) => {
        const apiDayName = dayNameToApiDay(dayName);
        const startTime = generateTimeString(dayData.startTime, constraints.startTime);
        const endTime = generateTimeString(dayData.endTime, constraints.endTime);
        allAvailabilitySlots.push({
          day: apiDayName,
          startTime,
          endTime,
        });
      });

    // Build instances array with floorId
    // floorId is stored when user selects a floor (via SpaceInstancesCard) or loaded from GET response
    // For PATCH/update: Include floorId if available
    let spaceInstances = formData.instances.map(instance => {
      const instancePayload = {
        ...(instance.apiId && { id: instance.apiId }), // Include id for existing instances (PATCH)
        name: instance.name,
        location: {
          name: instance.name,
          buildingAddressId: instance.buildingAddressId,
        },
      };

      // Include floorId if available (from GET response or user selection)
      // Only include for PATCH/update (when apiId exists) or when explicitly set
      if (instance.floorId) {
        instancePayload.floorId = instance.floorId;
      }

      return instancePayload;
    });

    logger.log("Instances being sent to API:", spaceInstances);
    logger.log("Deleted instance IDs:", deletedInstanceIds);

    let processedImages = [];
    const imagesToUpload = formData.images.filter(image => image.file);

    if (imagesToUpload.length > 0) {
      try {
        const filesToUpload = imagesToUpload.map(image => image.file);
        const uploadResponse = await spaceManagementService.uploadMedia(filesToUpload);
        if (uploadResponse.status === 200 || uploadResponse.status === 201) {
          processedImages = uploadResponse.data;
        } else {
          throw new Error("Failed to upload images");
        }
      } catch (error) {
        logger.error("Error uploading images during form submission:", error);
        throw new Error(`Image upload failed: ${error.message}`);
      }
    }

    const existingImages = formData.images
      .filter(image => !image.file && image.url && !image.url.startsWith("blob:"))
      .map(image => image.url);

    const firstFloorLevel = formData.instances.length > 0 ? parseInt(formData.instances[0].floorLevel) || 0 : 0;

    const formatSize = sizeInput => {
      if (!sizeInput) return `${formData.capacity || 1} person space`;

      if (
        sizeInput.toLowerCase().includes("sqm") ||
        sizeInput.toLowerCase().includes("sq") ||
        sizeInput.toLowerCase().includes("ft") ||
        sizeInput.toLowerCase().includes("m²")
      ) {
        return sizeInput;
      }

      const numberMatch = sizeInput.match(/[\d.]+/);
      if (numberMatch) {
        return `${numberMatch[0]} sqm`;
      }

      return sizeInput;
    };

    // Ensure categoryId is set (fallback to finding it from category code)
    let categoryId = formData.categoryId;
    if (!categoryId && formData.category) {
      const foundCategory = serviceCategories.find(
        cat => cat.code === formData.category || cat.name === formData.category
      );
      categoryId = foundCategory?.id;
      logger.log("🔍 [DEBUG] Fallback: Found categoryId from category code:", formData.category, "->", categoryId);
    }

    const payload = {
      size: formatSize(formData.size),
      media: {
        images: [...existingImages, ...processedImages],
        videos: [], // No videos for now
      },
      currency: formData.currency.toUpperCase(),
      ratePrice: parseFloat(formData.basePrice) || 0,
      description: formData.description,
      amenityIds: formData.amenities || [],
      spaceType: "LOW", // Always pass LOW as default
      capacity: parseInt(formData.capacity) || 1,
      duration: formData.bookingDuration.toUpperCase(),
      hostDisplayName: formData.hostDisplayName || undefined,
      hostDisplayImage: formData.hostDisplayImage || undefined,
      visibility:
        currentUser?.organization?.type?.code === "HOST_PROPERTY_DISTRIBUTION"
          ? "PUBLIC"
          : formData.isPublic
            ? "PUBLIC"
            : "PRIVATE",
      visibleToCorporate: formData.visibleToCorporate ?? false,
      visibleToConsumer: formData.visibleToConsumer ?? false,
      addons: formData.addons || [],
      numberOfInstances: spaceInstances.length,
      instances: spaceInstances,
      ...(isEditMode && deletedInstanceIds.length > 0 && { instancesToRemove: deletedInstanceIds }),
      availabilitySlots: allAvailabilitySlots,
      minimumDuration: parseInt(formData.minDuration) || 1,
      maximumDuration: parseInt(formData.maxDuration) || 24,
      bufferTimeInMinutes: parseInt(formData.bufferTime) ?? 30,
      subCategoryId: parseInt(formData.subCategoryId),
      zoneId: null,
    };

    // Note: Tickets are only for PLAY services (handled in play-services flow)

    // Debug logging for payload structure
    logger.log("🔍 [DEBUG] Built API payload:", JSON.stringify(payload, null, 2));
    logger.log("🔍 [DEBUG] Payload validation:");
    logger.log("- size:", payload.size);
    logger.log("- media.images:", payload.media.images.length, "images");
    logger.log("- currency:", payload.currency);
    logger.log("- ratePrice:", payload.ratePrice, typeof payload.ratePrice);
    logger.log("- description:", payload.description);
    logger.log("- amenityIds:", payload.amenityIds.length, "amenities");
    logger.log("- spaceType:", payload.spaceType);
    logger.log("- capacity:", payload.capacity, typeof payload.capacity);
    logger.log("- duration:", payload.duration);
    logger.log("- visibility:", payload.visibility);
    logger.log("- addons:", payload.addons.length, "addons");
    logger.log("- numberOfInstances:", payload.numberOfInstances, typeof payload.numberOfInstances);
    logger.log("- instances:", payload.instances.length, "instances");
    logger.log("- availabilitySlots:", payload.availabilitySlots.length, "slots");
    logger.log("- minimumDuration:", payload.minimumDuration, typeof payload.minimumDuration);
    logger.log("- maximumDuration:", payload.maximumDuration, typeof payload.maximumDuration);
    logger.log("- bufferTimeInMinutes:", payload.bufferTimeInMinutes, typeof payload.bufferTimeInMinutes);
    logger.log("- subCategoryId:", payload.subCategoryId, typeof payload.subCategoryId);
    logger.log("- zoneId:", payload.zoneId);
    const ticketCount = Array.isArray(payload.tickets) ? payload.tickets.length : 0;
    logger.log("- ticketType:", payload.ticketType ?? "<none>");
    logger.log("- tickets:", ticketCount, "tickets");

    // Validate required fields (excluding optional arrays and null values)
    const requiredFields = [
      "size",
      "currency",
      "ratePrice",
      "description",
      "media",
      "instances",
      "numberOfInstances",
      "spaceType",
      "subCategoryId",
      "duration",
      "visibility",
      "availabilitySlots",
      "minimumDuration",
      "maximumDuration",
      "bufferTimeInMinutes",
      "capacity",
    ];

    const missingFields = requiredFields.filter(field => {
      const value = payload[field];
      return value === undefined || value === null || value === "" || (typeof value === "number" && isNaN(value));
    });

    if (missingFields.length > 0) {
      logger.error("❌ [ERROR] Missing or invalid required fields:", missingFields);
      throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
    }

    // Validate data types
    const typeValidations = [
      { field: "ratePrice", type: "number", value: payload.ratePrice },
      { field: "numberOfInstances", type: "number", value: payload.numberOfInstances },
      { field: "subCategoryId", type: "number", value: payload.subCategoryId },
      { field: "minimumDuration", type: "number", value: payload.minimumDuration },
      { field: "maximumDuration", type: "number", value: payload.maximumDuration },
      { field: "bufferTimeInMinutes", type: "number", value: payload.bufferTimeInMinutes },
      { field: "capacity", type: "number", value: payload.capacity },
      { field: "media", type: "object", value: payload.media },
      { field: "amenityIds", type: "object", value: payload.amenityIds },
      { field: "addons", type: "object", value: payload.addons },
      { field: "instances", type: "object", value: payload.instances },
      { field: "availabilitySlots", type: "object", value: payload.availabilitySlots },
    ];

    const typeErrors = typeValidations.filter(({ field, type, value }) => {
      if (type === "number") return typeof value !== "number" || isNaN(value);
      if (type === "object") {
        // Special handling for media field which is an object, not an array
        if (field === "media") {
          return typeof value !== "object" || value === null || !value.images || !Array.isArray(value.images);
        }
        // For other object fields, expect arrays
        return !Array.isArray(value);
      }
      return false;
    });

    if (typeErrors.length > 0) {
      logger.error("❌ [ERROR] Type validation errors:", typeErrors);
      throw new Error(`Type validation failed for fields: ${typeErrors.map(e => e.field).join(", ")}`);
    }

    logger.log("✅ [DEBUG] Payload validation passed");

    return payload;
  };

  const onSubmit = async formData => {
    logger.log("🚀 [DEBUG] onSubmit called", { isEditMode, spaceId, formData });
    logger.log("onSubmit called", { isEditMode, spaceId, formData });
    try {
      setLoading(true);
      const currentUser = getUser();
      if (!currentUser) {
        throw new Error("User not found");
      }

      logger.log("🔍 [DEBUG] Building API payload...");
      logger.log("Building API payload...");
      const payload = await buildApiPayload(formData);
      logger.log("🔍 [DEBUG] Submitting space payload:", payload);
      logger.log("Submitting space payload:", payload);

      let response;
      if (isEditMode && spaceId) {
        logger.log("🔍 [DEBUG] About to call updateSpace API", { spaceId, payload });
        logger.log("About to call updateSpace API", { spaceId, payload });
        // Update existing space
        logger.log("🔍 [DEBUG] Calling spaceManagementService.updateSpace...");
        logger.log("Calling spaceManagementService.updateSpace...");
        response = await spaceManagementService.updateSpace(parseInt(spaceId), payload, currentUser.id);
        logger.log("🔍 [DEBUG] updateSpace API response:", response);
        logger.log("updateSpace API response:", response);
      } else {
        // Create new space
        logger.log("🔍 [DEBUG] About to call createSpace API", { payload });
        logger.log("🔍 [DEBUG] Calling spaceManagementService.createSpace...");
        response = await spaceManagementService.createSpace(payload);
        logger.log("🔍 [DEBUG] createSpace API response:", response);
      }

      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, isEditMode ? "update space" : "create space");

        // Clear saved form data on successful submission
        try {
          sessionStorage.removeItem("ADD_SPACE_FORM_DATA");
          logger.log("✅ Cleared saved form data after successful submission");
        } catch (e) {
          logger.warn("⚠️ Failed to clear saved form data:", e);
        }

        setTimeout(() => {
          history.push("/host/spaces");
        }, 1500);
      } else {
        showApiMessage(response, isEditMode ? "update space" : "create space");
      }
    } catch (error) {
      logger.error("Error saving space:", error);

      let errorMessage = "Unknown error occurred";
      let errorDescription = "Please try again or contact support if the problem persists.";

      // Check for API validation errors first
      if (error.response?.data) {
        const apiData = error.response.data;

        // Handle different types of API error responses
        if (apiData.validationErrors && Array.isArray(apiData.validationErrors)) {
          // Show validation errors from API
          const validationMessages = apiData.validationErrors.map(err => err.message || err).join(", ");
          errorMessage = "Validation errors";
          errorDescription = validationMessages;
        } else if (apiData.errors && Array.isArray(apiData.errors)) {
          // Show general errors from API
          const errorMessages = apiData.errors.map(err => err.message || err).join(", ");
          errorMessage = "API errors";
          errorDescription = errorMessages;
        } else if (apiData.message) {
          // Show API message
          errorMessage = "API error";
          errorDescription = apiData.message;
        } else if (apiData.details) {
          // Show API details
          errorMessage = "API error";
          errorDescription = apiData.details;
        }
      } else if (error.message) {
        if (error.message.includes("User not found")) {
          errorMessage = "Authentication error";
          errorDescription = "Please log in again and try saving the space.";
        } else if (error.message.includes("Image upload failed")) {
          errorMessage = "Image upload failed";
          errorDescription = "Failed to upload images. Please check your images and try again.";
        } else if (error.message.includes("network") || error.message.includes("fetch")) {
          errorMessage = "Network error";
          errorDescription = "Please check your internet connection and try again.";
        } else {
          errorMessage = isEditMode ? "Space update failed" : "Space creation failed";
          errorDescription = error.message;
        }
      }

      toastUtils.error(errorMessage, {
        description: errorDescription,
        action: {
          label: "Contact Support",
          onClick: () => {
            window.open("mailto:support@spacia.page", "_blank");
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = isEditMode ? spaceData?.instances?.[0]?.title || "" : "";

  const submitButtonText = isEditMode
    ? loading
      ? "Updating..."
      : "Update Space"
    : loading
      ? "Creating..."
      : "Create Space";

  // if (fetchingSpace) {
  //   return (
  //     <HPageContainer>
  //       <HHeader />
  //       <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
  //         <div className="space-y-6 animate-pulse">
  //           <div className="p-6 bg-white rounded-lg border">
  //             <div className="space-y-4">
  //               <div className="w-1/3 h-6 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="grid grid-cols-2 gap-4">
  //                 <div className="h-6 rounded bg-slate-200"></div>
  //                 <div className="h-6 rounded bg-slate-200"></div>
  //                 <div className="h-6 rounded bg-slate-200"></div>
  //                 <div className="h-6 rounded bg-slate-200"></div>
  //               </div>
  //               <div className="w-full h-24 rounded bg-slate-200"></div>
  //             </div>
  //           </div>
  //           <div className="p-6 bg-white rounded-lg border">
  //             <div className="space-y-4">
  //               <div className="w-1/3 h-6 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //             </div>
  //           </div>
  //           <div className="p-6 bg-white rounded-lg border">
  //             <div className="space-y-4">
  //               <div className="w-1/3 h-6 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //               <div className="w-full h-10 rounded bg-slate-200"></div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </HPageContainer>
  //   );
  // }

  const handleDisplayImageUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDisplayImage(true);
    try {
      const response = await spaceManagementService.uploadMedia([file]);
      if ((response.status === 200 || response.status === 201) && response.data && response.data.length > 0) {
        const imageUrl = response.data[0];
        setValue("hostDisplayImage", imageUrl, { shouldDirty: true });
        showApiMessage(response, "upload display image");
      } else {
        showApiMessage(response, "upload display image");
      }
    } catch (error) {
      showApiMessage(error, "upload display image");
    } finally {
      setUploadingDisplayImage(false);
      if (displayImageInputRef.current) displayImageInputRef.current.value = "";
    }
  };

  const handleRemoveDisplayImage = () => {
    setValue("hostDisplayImage", "", { shouldDirty: true });
  };

  return (
    <HPageContainer>
      <HHeader
        leftButton={
          <Button variant="ghost" onClick={() => history.goBack()} className="flex gap-2 items-center">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        }
        rightButton={
          isEditMode && (
            <div className="flex gap-2 items-center">
              {isReadOnly ? (
                <Button variant="outline" onClick={toggleEditMode} className="flex gap-2 items-center">
                  <Edit3 className="w-4 h-4" />
                  Edit
                </Button>
              ) : (
                <Button variant="outline" onClick={toggleEditMode} className="flex gap-2 items-center">
                  <Eye className="w-4 h-4" />
                  View Only
                </Button>
              )}
              {spaceData && (
                <Button
                  variant="outline"
                  onClick={handlePublishToggle}
                  disabled={loading || publishing}
                  className="flex gap-2 items-center"
                >
                  {publishing ? "..." : spaceData.status === "PUBLISHED" ? "Unpublish" : "Publish"}
                </Button>
              )}
              <Button
                variant="destructive"
                onClick={handleDeleteSpace}
                disabled={loading}
                className="flex gap-2 items-center"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          )
        }
      />

      <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
        {isEditMode && (
          <div className="flex gap-2 items-center mb-6">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isReadOnly ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"
              }`}
            >
              {isReadOnly ? "Read Only Mode" : "Edit Mode"}
            </span>
            {spaceData && (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  spaceData.status === "PUBLISHED" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {spaceData.status}
              </span>
            )}
          </div>
        )}

        <FormProvider {...{ control, handleSubmit, formState: { errors }, setValue, getValues, watch, trigger, reset }}>
          <form
            onSubmit={handleSubmit(data => {
              logger.log("🔍 [DEBUG] Form submission triggered with data:", data);
              logger.log("🔍 [DEBUG] Form errors:", errors);
              logger.log("🔍 [DEBUG] Form is valid:", Object.keys(errors).length === 0);
              return onSubmit(data);
            })}
          >
            {/* Debug: Show all form errors - Only in Edit Mode */}
            {Object.keys(errors).length > 0 && !isReadOnly && (
              <div className="p-4 mb-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="mb-2 text-sm font-medium text-red-800">Form Validation Errors:</h3>
                <div className="space-y-1">
                  {Object.entries(errors).map(([fieldName, error]) => {
                    // Handle nested errors for instances array
                    if (fieldName === "instances" && Array.isArray(error)) {
                      return error.map((instanceError, idx) => {
                        if (!instanceError) return null;

                        const buildingId = watchedInstances?.[idx]?.buildingAddressId;
                        const isLoadingFloorsForThisBuilding = buildingId
                          ? !buildingFloors[buildingId] || !!loadingFloors[buildingId]
                          : false;

                        const relevantErrors = Object.entries(instanceError)
                          .filter(([key, e]) => {
                            // Suppress floor selection errors if floors are still loading
                            if ((key === "floorId" || key === "floorLevel") && isLoadingFloorsForThisBuilding) {
                              return false;
                            }
                            return true;
                          })
                          .map(([_, e]) => e.message);

                        if (relevantErrors.length === 0) return null;

                        return (
                          <div key={`${fieldName}-${idx}`} className="text-sm text-red-600">
                            <strong>Space Unit {idx + 1}:</strong> {relevantErrors.join(", ")}
                          </div>
                        );
                      });
                    }
                    return (
                      <div key={fieldName} className="text-sm text-red-600">
                        <strong>{fieldName}:</strong> {error?.message || "Invalid field"}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========== FORM SECTIONS ========== */}
            {/* Comment out any section below to hide it from the form */}
            <div className="space-y-6">
              {/* Section 1: Space Type & Attributes */}
              {(() => {
                const spaceTypeSection = (
                  <SpaceTypeAttributesCard
                    control={control}
                    errors={errors}
                    dragActive={dragActive}
                    fileInputRef={fileInputRef}
                    handleDrag={handleDrag}
                    handleDrop={handleDrop}
                    handleFileSelect={handleFileSelect}
                    removeImage={removeImage}
                    watchedImages={watchedImages}
                    isReadOnly={isReadOnly}
                    lockedCategory={lockedCategory}
                    serviceCategories={serviceCategories}
                    forceSubcategoryLoad={forceSubcategoryLoad}
                    categoriesLoading={serviceCategoriesLoading}
                  />
                );
                return spaceTypeSection;
              })()}

              {/* Section 2: Pricing & Booking Rules */}
              {(() => {
                const pricingSection = (
                  <PricingBookingRulesCard control={control} errors={errors} isReadOnly={isReadOnly} />
                );
                return pricingSection;
              })()}

              {/* Section 2.5: Presented By */}
              {(() => {
                const hostDisplaySection = (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Presented By</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="hostDisplayName">Display Name</Label>
                        <Controller
                          name="hostDisplayName"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="hostDisplayName"
                              placeholder="e.g., Acme Corp Workspaces"
                              disabled={isReadOnly}
                              {...field}
                            />
                          )}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Display Image</Label>
                        <input
                          type="file"
                          accept="image/*"
                          ref={displayImageInputRef}
                          onChange={handleDisplayImageUpload}
                          className="hidden"
                          disabled={isReadOnly}
                        />
                        <div className="flex flex-col gap-4 items-start">
                          <label className="flex relative justify-center items-center w-20 h-20 text-gray-400 bg-gray-100 rounded-lg border-2 border-gray-300 border-dashed transition cursor-pointer hover:border-blue-400">
                            {watch("hostDisplayImage") ? (
                              <img
                                src={watch("hostDisplayImage")}
                                alt="Display"
                                className="object-cover w-20 h-20 rounded-lg"
                              />
                            ) : (
                              <Image className="w-8 h-8" />
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleDisplayImageUpload}
                              disabled={isReadOnly}
                            />
                            {uploadingDisplayImage && (
                              <div className="flex absolute inset-0 justify-center items-center bg-white bg-opacity-70 rounded-lg">
                                <span className="text-xs text-gray-600">Uploading...</span>
                              </div>
                            )}
                          </label>
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <label className="flex gap-2 items-center px-4 py-2 w-max text-sm font-medium bg-white rounded-md border border-gray-300 cursor-pointer hover:bg-gray-50">
                                <Upload size={17} />
                                <span>{watch("hostDisplayImage") ? "Change Image" : "Upload Image"}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={handleDisplayImageUpload}
                                  disabled={isReadOnly}
                                />
                              </label>
                              {watch("hostDisplayImage") && (
                                <button
                                  type="button"
                                  onClick={handleRemoveDisplayImage}
                                  disabled={isReadOnly}
                                  className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-red-600 bg-white rounded-md border border-red-200 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 size={17} />
                                  <span>Remove</span>
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">Recommended: 512x512px, PNG or SVG</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
                return hostDisplaySection;
              })()}

              {/* Section 3: Space Instances */}
              {(() => {
                const instancesSection = (
                  <SpaceInstancesCard
                    control={control}
                    errors={errors}
                    instanceFields={instanceFields}
                    addInstance={addInstance}
                    removeInstance={removeInstanceAtIndex}
                    isReadOnly={isReadOnly}
                    fetchingSpace={fetchingSpace}
                    buildingFloors={buildingFloors}
                    loadingFloors={loadingFloors}
                    setBuildingFloors={setBuildingFloors}
                    fetchFloorsForBuilding={fetchFloorsForBuilding}
                  />
                );
                return instancesSection;
              })()}

              {/* Section 4: Space Addons */}
              {(() => {
                const watchedCategoryId = watch("categoryId");
                const watchedDuration = watch("duration") || "HOURLY";
                const addonsSection = (
                  <SpaceAddonsCard
                    control={control}
                    errors={errors}
                    isReadOnly={isReadOnly}
                    categoryId={watchedCategoryId}
                    spaceDurationType={watchedDuration}
                  />
                );

                return addonsSection;
              })()}

              {/* Section 5: Global Settings */}
              {(() => {
                const globalSettingsSection = (
                  <GlobalSettingsCard
                    control={control}
                    errors={errors}
                    isReadOnly={isReadOnly}
                    originalAvailability={originalAvailability}
                  />
                );
                return globalSettingsSection;
              })()}

              {/* Floor plan card removed: floor plans are now managed under Building Address floors */}

              {/* Section 6: Form Actions */}
              <div className="flex justify-end pt-4 space-x-3">
                <Button variant="outline" onClick={() => history.goBack()} type="button">
                  {isEditMode ? "Cancel" : "Cancel"}
                </Button>
                {!isReadOnly && (
                  <>
                    <Button
                      type="submit"
                      variant="spaciadefault"
                      disabled={loading}
                      onClick={async e => {
                        // Prevent default form submission and handle manually
                        e.preventDefault();
                        e.stopPropagation();

                        logger.log("Update Space button clicked - manual submission");

                        try {
                          // First try the normal form submission
                          const formData = getValues();
                          logger.log("Attempting normal form submission with data:", formData);

                          // Validate form manually and show errors inline
                          const formDataToValidate = getValues();

                          // Clean the form data to ensure proper types
                          const cleanedFormData = {
                            ...formDataToValidate,
                            amenities: Array.isArray(formDataToValidate.amenities)
                              ? formDataToValidate.amenities.filter(id => typeof id === "number")
                              : [],
                            addons: Array.isArray(formDataToValidate.addons)
                              ? formDataToValidate.addons.filter(id => typeof id === "number")
                              : [],
                          };

                          logger.log("Form data being validated:", cleanedFormData);
                          logger.log("Amenities array:", formDataToValidate.amenities);
                          logger.log("Addons array:", formDataToValidate.addons);
                          logger.log("Amenities type:", typeof formDataToValidate.amenities);
                          logger.log("Addons type:", typeof formDataToValidate.addons);

                          const isValid = await trigger();
                          if (!isValid) {
                            logger.error("Form validation failed");
                            logger.log("Form validation errors:", errors);

                            // Force the form to show all validation errors immediately
                            await trigger(undefined, { shouldFocus: true });

                            // Log errors again after trigger
                            logger.log("Errors after trigger:", errors);

                            // Force a re-render to show errors
                            setTimeout(() => {
                              trigger();
                            }, 100);

                            // Show a simple toast to indicate validation failed
                            toastUtils.error("Form validation failed", {
                              description: "Please fix the errors shown below and try again.",
                            });

                            // Scroll to first error field
                            const firstErrorField = Object.keys(errors)[0];
                            if (firstErrorField) {
                              const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
                              if (errorElement) {
                                setTimeout(() => {
                                  errorElement.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                  });
                                  errorElement.focus();
                                }, 100);
                              }
                            }

                            return;
                          }

                          // Call onSubmit directly with cleaned data
                          await onSubmit(cleanedFormData);
                        } catch (error) {
                          logger.error("Form submission failed, trying alternative method:", error);

                          // Fallback: try with minimal payload
                          try {
                            const currentUser = getUser();
                            if (!currentUser) {
                              throw new Error("User not found");
                            }

                            const formData = getValues();
                            const fallbackPayload = await buildApiPayload(formData);

                            logger.log("Trying fallback update with payload:", fallbackPayload);

                            const response = await spaceManagementService.updateSpace(
                              parseInt(spaceId),
                              fallbackPayload,
                              currentUser.id
                            );

                            logger.log("Fallback update successful:", response);
                            toastUtils.success("Space updated successfully!", {
                              description: "Your space changes have been saved.",
                            });

                            setTimeout(() => {
                              history.push("/host/spaces");
                            }, 1500);
                          } catch (fallbackError) {
                            logger.error("Fallback update also failed:", fallbackError);
                            toastUtils.error("Failed to update space", {
                              description: "Please try again or contact support.",
                            });
                          }
                        }
                      }}
                    >
                      {submitButtonText}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </FormProvider>
      </div>

      {/* Delete Space Confirmation Modal */}
      <DeleteSpaceModal
        show={showDeleteModal}
        onClose={cancelDeleteSpace}
        onConfirm={confirmDeleteSpace}
        isLoading={deleting}
        spaceName={spaceData?.instances?.[0]?.title || "this space"}
      />
    </HPageContainer>
  );
};

export default HostAddSpace;
